import React from 'react';
import { CheckCircle, Building2, User, Mail, Phone, Key, Calendar, CreditCard, Copy, X } from 'lucide-react';
import { getPlanById } from '../../../../config/subscriptionPlans';

const RegistrationSuccessModal = ({ company, onClose }) => {
    const plan = getPlanById(company.subscriptionPlan);

    // Use credentials from backend response if available, otherwise fallback to generated ones
    const username = company.credentials?.username || company.email?.split('@')[0] || 'N/A';
    const tempPassword = company.credentials?.password || 'TempPassword@123';
    const email = company.credentials?.email || company.email;

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        alert(`${label} copied to clipboard!`);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col animate-slideUp">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-6 text-center relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all"
                    >
                        <X size={18} className="text-white" />
                    </button>
                    <div className="flex justify-center mb-3">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <CheckCircle size={40} className="text-white" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        Company Registered Successfully! 🎉
                    </h2>
                    <p className="text-green-100">
                        {company.name} has been added to the system
                    </p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                    {/* Company Details */}
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-3 border border-blue-100">
                        <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                            <Building2 size={16} className="text-blue-600" />
                            Company Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div>
                                <span className="text-gray-600">Company Name:</span>
                                <p className="font-semibold text-gray-900">{company.name}</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Trade License:</span>
                                <p className="font-semibold text-gray-900">{company.tradeLicenseNo}</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Email:</span>
                                <p className="font-semibold text-gray-900">{company.email}</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Phone:</span>
                                <p className="font-semibold text-gray-900">{company.phone}</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Subscription Plan:</span>
                                <p className={`font-semibold inline-flex items-center px-2 py-1 rounded-full text-xs ${plan?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                                    plan?.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                                        plan?.color === 'green' ? 'bg-green-100 text-green-700' :
                                            'bg-gray-100 text-gray-700'
                                    }`}>
                                    <CreditCard size={12} className="mr-1" />
                                    {plan?.name}
                                </p>
                            </div>
                            <div>
                                <span className="text-gray-600">Expires On:</span>
                                <p className="font-semibold text-gray-900 flex items-center gap-1">
                                    <Calendar size={12} />
                                    {new Date(company.subscriptionExpiryDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Login Credentials */}
                    <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-3 border border-orange-200">
                        <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                            <Key size={16} className="text-orange-600" />
                            Owner Login Credentials
                        </h3>
                        <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 border border-orange-100">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-gray-600 font-semibold uppercase">Username</span>
                                    <button
                                        onClick={() => copyToClipboard(username, 'Username')}
                                        className="text-orange-600 hover:text-orange-700 transition-colors"
                                        title="Copy username"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                                <p className="font-mono font-bold text-gray-900 text-lg">{username}</p>
                            </div>

                            <div className="bg-white rounded-lg p-3 border border-orange-100">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-gray-600 font-semibold uppercase">Temporary Password</span>
                                    <button
                                        onClick={() => copyToClipboard(tempPassword, 'Password')}
                                        className="text-orange-600 hover:text-orange-700 transition-colors"
                                        title="Copy password"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                                <p className="font-mono font-bold text-gray-900 text-lg">{tempPassword}</p>
                            </div>

                            <div className="bg-orange-100 border border-orange-200 rounded-lg p-3">
                                <p className="text-xs text-orange-800 flex items-start gap-2">
                                    <span className="text-orange-600 font-bold">⚠️</span>
                                    <span>
                                        <strong>Important:</strong> The owner must change this temporary password on first login for security purposes.
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contact Person */}
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-1.5 flex items-center gap-2 text-sm">
                            <User size={16} className="text-gray-600" />
                            Primary Contact
                        </h3>
                        <p className="text-gray-900 font-semibold text-sm">{company.contactPerson}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                                <Mail size={12} />
                                {company.email}
                            </span>
                            <span className="flex items-center gap-1">
                                <Phone size={12} />
                                {company.phone}
                            </span>
                        </div>
                    </div>

                    {/* Next Steps */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                        <h3 className="font-bold text-blue-900 mb-1.5 text-sm">📋 Next Steps</h3>
                        <ol className="text-xs text-blue-800 space-y-0.5 list-decimal list-inside">
                            <li>Share the login credentials with the company owner</li>
                            <li>Owner should login and change the temporary password</li>
                            <li>Owner can then invite additional users to the system</li>
                            <li>Configure company settings and preferences</li>
                        </ol>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RegistrationSuccessModal;
